import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Mail, Phone, Pencil } from "lucide-react";

export function KeyContactsPanel({ personId, organizationId, canManage }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    api.get(`/key-contacts/resolved/${personId}`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [personId]);
  useEffect(() => { load(); }, [load]);

  if (items === null || items.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="key-contacts-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-[#0D9488]/10 text-[#0D9488] flex items-center justify-center"><LifeBuoy className="w-4 h-4" /></span>
          <h3 className="font-display font-semibold text-slate-900">Need Help? Your District Key Contacts</h3>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="key-contacts-edit">
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((it) => (
          <div key={it.topic_id} className="py-2.5 flex items-start justify-between gap-4" data-testid={`kc-row-${it.topic_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">{it.topic_name}</p>
              <p className="text-sm text-slate-600">{it.contact.name}{it.contact.title ? ` · ${it.contact.title}` : ""}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {it.contact.email && (
                <a href={`mailto:${it.contact.email}`} className="text-[#0093D6] hover:text-[#006090]" title={it.contact.email} data-testid={`kc-email-${it.topic_id}`}>
                  <Mail className="w-4 h-4" />
                </a>
              )}
              {it.contact.phone && (
                <a href={`tel:${it.contact.phone.replace(/[^\d+]/g, "")}`} className="text-[#0093D6] hover:text-[#006090]" title={it.contact.phone} data-testid={`kc-phone-${it.topic_id}`}>
                  <Phone className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
      {editing && <OverrideDialog personId={personId} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}

function OverrideDialog({ personId, onClose, onSaved }) {
  const [rows, setRows] = useState(null);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const ov = await api.get(`/key-contacts/overrides/${personId}`);
      setRows(ov.data.items || []);
      if (ov.data.organization_id) {
        const s = await api.get("/key-contacts/staff", { params: { organization_id: ov.data.organization_id } });
        setStaff(s.data.items || []);
      }
    })().catch((e) => toast.error(apiError(e)));
  }, [personId]);

  const setRow = (topicId, value) => setRows((prev) => prev.map((r) => {
    if (r.topic_id !== topicId) return r;
    if (value === "__default__") return { ...r, mode: "default", contact_person_id: null };
    if (value === "__none__") return { ...r, mode: "not_assigned", contact_person_id: null };
    return { ...r, mode: "person", contact_person_id: value };
  }));

  const valueOf = (r) => r.mode === "default" ? "__default__" : r.mode === "not_assigned" ? "__none__" : (r.contact_person_id || "__default__");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/key-contacts/overrides/${personId}`, {
        overrides: rows.map((r) => ({ topic_id: r.topic_id, mode: r.mode, contact_person_id: r.contact_person_id })),
      });
      toast.success("Key Contacts updated");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto tr-scroll" data-testid="kc-override-dialog">
        <DialogHeader>
          <DialogTitle>Edit Key Contacts</DialogTitle>
          <DialogDescription>Override individual topics for this Agent. Others use the District default.</DialogDescription>
        </DialogHeader>
        {rows === null ? <p className="text-sm text-slate-400">Loading…</p> : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.topic_id} className="grid grid-cols-2 gap-3 items-center">
                <span className="text-sm text-slate-700">{r.topic_name}</span>
                <Select value={valueOf(r)} onValueChange={(v) => setRow(r.topic_id, v)}>
                  <SelectTrigger data-testid={`kc-ov-${r.topic_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__default__">Use District Default</SelectItem>
                    <SelectItem value="__none__">Not Assigned</SelectItem>
                    {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="kc-override-save" className="bg-[#006090] hover:bg-[#004E75]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
